import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Check, MonitorPlay, Package, MessageCircle } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { usePortalLanguage } from "@/contexts/PortalLanguageContext";
import { useTurnstile } from "@/lib/turnstile";

// ── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";

// ── Lead capture (additive, non-blocking — same pattern as public/demo.html) ──
const SB_URL     = import.meta.env.VITE_SUPABASE_URL  as string;
const SB_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const HS_PORTAL_ID = "342637287";
const HS_FORM_GUID = "33a606e2-ecb5-4c61-8e92-3a1e0ab61f47";

interface DemoLeadFields {
  first_name: string; last_name: string; store_name: string; email: string;
  phone: string; city: string; state: string;
  catalogue_size: string; referral: string; message: string;
}

// Bot traps for create-demo-lead (see supabase/functions/_shared/formGuard.ts).
const PAGE_OPENED_AT = Date.now();

function createDemoLead(fields: DemoLeadFields, hp = "", turnstileToken = "") {
  fetch(`${SB_URL}/functions/v1/create-demo-lead?apikey=${encodeURIComponent(SB_ANON_KEY)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: SB_ANON_KEY, Authorization: `Bearer ${SB_ANON_KEY}` },
    body: JSON.stringify({ ...fields, _hp: hp, elapsed_ms: Date.now() - PAGE_OPENED_AT, turnstile_token: turnstileToken }),
  }).catch((err) => console.error("create-demo-lead error:", err));
}

function postToHubSpot(fields: DemoLeadFields) {
  fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_FORM_GUID}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fields: [
        { name: "firstname",       value: fields.first_name },
        { name: "lastname",        value: fields.last_name },
        { name: "company",         value: fields.store_name },
        { name: "email",           value: fields.email },
        { name: "phone",           value: fields.phone },
        { name: "city",            value: fields.city },
        { name: "state",           value: fields.state },
        { name: "catalogue_size",  value: fields.catalogue_size },
        { name: "referral",        value: fields.referral },
        { name: "message",         value: fields.message },
      ],
      context: { pageUri: window.location.href, pageName: "Fyrris Retailers Demo Page" },
    }),
  }).catch((err) => console.error("HubSpot submission error:", err));
}

// ── Dropdown options ──────────────────────────────────────────────────────────
const CATALOGUE_SIZES = ["Under 25", "25–100", "100–250", "250+"] as const;
type CatalogueSize = typeof CATALOGUE_SIZES[number] | "";

const REFERRAL_OPTIONS = [
  "Google",
  "Social Media",
  "HPB Trade Show",
  "Manufacturer referral",
  "Friend / colleague",
  "Other",
] as const;
type ReferralOption = typeof REFERRAL_OPTIONS[number] | "";

// ── Form state ────────────────────────────────────────────────────────────────
interface FormState {
  firstName:     string;
  lastName:      string;
  storeName:     string;
  email:         string;
  phone:         string;
  city:          string;
  stateProvince: string;
  catalogueSize: CatalogueSize;
  referral:      ReferralOption;
  message:       string;
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

const BLANK: FormState = {
  firstName: "", lastName: "", storeName: "", email: "",
  phone: "", city: "", stateProvince: "", catalogueSize: "",
  referral: "", message: "",
};

// ── Input class helper ────────────────────────────────────────────────────────
const inputCls = (error?: string) =>
  [
    "w-full rounded-lg border px-3 py-2.5 text-sm bg-gray-900 text-white placeholder-gray-500",
    "focus:outline-none focus:ring-2 focus:border-transparent transition",
    error
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-700 focus:ring-orange-500",
  ].join(" ");

// ── Component ─────────────────────────────────────────────────────────────────
export default function RetailersDemo() {
  const { t } = usePortalLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  const [form,        setForm]        = useState<FormState>(BLANK);
  const [errors,      setErrors]      = useState<FieldErrors>({});
  const [busy,        setBusy]        = useState(false);
  const [success,     setSuccess]     = useState(false);
  const [booked,      setBooked]      = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const calInitialized = useRef(false);
  const [hp, setHp] = useState("");
  const turnstile = useTurnstile();

  // ── Cal.com embed (ported from public/demo.html — same namespace/link) ─────
  useEffect(() => {
    if (!success || booked || calInitialized.current) return;
    calInitialized.current = true;

    (function (C: any, A: string, L: string) {
      const p = (a: any, ar: any) => { a.q.push(ar); };
      const d = C.document;
      C.Cal = C.Cal || function (...args: any[]) {
        const cal = C.Cal; const ar = args;
        if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
        if (ar[0] === L) {
          const api: any = (...apiArgs: any[]) => { p(api, apiArgs); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") { cal.ns[namespace] = cal.ns[namespace] || api; p(cal.ns[namespace], ar); p(cal, ["initNamespace", namespace]); }
          else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    const Cal = (window as any).Cal;
    Cal("init", "retailers-demo", { origin: "https://cal.com" });
    Cal.ns["retailers-demo"]("inline", {
      elementOrSelector: "#cal-booking",
      calLink: "fyrris/retailers-demo",
      layout: "month_view",
    });
    Cal.ns["retailers-demo"]("ui", {
      styles: { branding: { brandColor: EMBER } },
      hideEventTypeDetails: false,
      layout: "month_view",
    });
    Cal.ns["retailers-demo"]("on", {
      action: "bookingSuccessful",
      callback: () => setBooked(true),
    });
  }, [success, booked]);

  // ── Field update ────────────────────────────────────────────────────────────
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    setServerError(null);
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.firstName.trim())  e.firstName    = t("demo.errorRequired", "Required");
    if (!form.lastName.trim())   e.lastName     = t("demo.errorRequired", "Required");
    if (!form.storeName.trim())  e.storeName    = t("demo.errorRequired", "Required");
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                                  e.email       = t("demo.errorEmail",    "Valid email required");
    if (!form.phone.trim())      e.phone        = t("demo.errorRequired", "Required");
    if (!form.city.trim())       e.city         = t("demo.errorRequired", "Required");
    if (!form.stateProvince.trim()) e.stateProvince = t("demo.errorRequired", "Required");
    return e;
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length) { setErrors(fieldErrors); return; }

    setBusy(true);
    setServerError(null);

    const fullName = `${form.firstName} ${form.lastName}`.trim();
    const messageBody = [
      form.message.trim(),
      `Store: ${form.storeName}`,
      `Phone: ${form.phone}`,
      `Location: ${form.city}, ${form.stateProvince}`,
      form.catalogueSize ? `Catalogue size: ${form.catalogueSize}` : "",
      form.referral      ? `Referral: ${form.referral}` : "",
    ].filter(Boolean).join("\n");

    // Additive, non-blocking — never awaited, never affects the outcome below.
    // Same fire-and-forget pattern as public/demo.html.
    const leadFields: DemoLeadFields = {
      first_name: form.firstName, last_name: form.lastName, store_name: form.storeName,
      email: form.email.trim(), phone: form.phone, city: form.city, state: form.stateProvince,
      catalogue_size: form.catalogueSize, referral: form.referral, message: form.message.trim(),
    };
    createDemoLead(leadFields, hp, await turnstile.getToken());
    postToHubSpot(leadFields);

    const { error: dbError } = await supabase
      .from("contact_messages")
      .insert({
        name:    fullName,
        email:   form.email.trim(),
        message: messageBody,
        source:  "demo_request",
      });

    setBusy(false);

    if (dbError) {
      setServerError(t("demo.errorServer", "Something went wrong. Please try again."));
      return;
    }

    setSuccess(true);
  };

  // ── Cal.com booking screen (shown right after successful form submit) ──────
  if (success && !booked) {
    return (
      <div
        className="min-h-screen flex flex-col items-center px-6 py-12"
        style={{ backgroundColor: NAVY }}
      >
        <Helmet>
          <title>{t("demo.meta.title", "Book a Fyrris Demo — 15 Minutes, No Commitment")}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: `${TEAL}22` }}
        >
          <Check className="w-7 h-7" style={{ color: TEAL }} />
        </div>
        <p
          className="text-xs font-semibold tracking-widest uppercase mb-8 text-center"
          style={{ color: TEAL, fontFamily: "'DM Mono', monospace" }}
        >
          {t("demo.calEyebrow", "You're in — now pick a time that works for you")}
        </p>
        <div className="w-full max-w-3xl">
          <div id="cal-booking" style={{ minWidth: 320, height: 700 }} />
        </div>
      </div>
    );
  }

  // ── "You're booked in" confirmation (after a real Cal.com booking) ─────────
  if (booked) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ backgroundColor: NAVY }}
      >
        <Helmet>
          <title>{t("demo.meta.title", "Book a Fyrris Demo — 15 Minutes, No Commitment")}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: `${TEAL}22` }}
        >
          <Check className="w-7 h-7" style={{ color: TEAL }} />
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">
          {t("demo.successTitle", "You're booked in.")}
        </h1>
        <p className="text-gray-400 max-w-sm leading-relaxed">
          {t("demo.successBody", "We'll reach out within 1 business day to confirm your demo time.")}
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-8 inline-flex items-center justify-center rounded-full px-8 py-3 text-sm font-medium text-white whitespace-nowrap transition hover:opacity-90"
          style={{ backgroundColor: EMBER }}
        >
          {t("demo.successCta", "Back to Home")}
        </button>
      </div>
    );
  }

  // ── Main layout ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#030712" }}>
      <Helmet>
        <title>{t("demo.meta.title", "Book a Fyrris Demo — 15 Minutes, No Commitment")}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* ── Left brand panel (desktop only) ─────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-[420px] xl:w-[480px] flex-shrink-0 flex-col justify-between p-12"
        style={{ backgroundColor: NAVY }}
      >
        <img src="/fyrris-logo.png" alt="Fyrris" className="h-9 w-auto object-contain" />

        <div className="space-y-8">
          <div>
            <p
              className="text-xs font-semibold tracking-widest uppercase mb-4"
              style={{ color: EMBER }}
            >
              {t("demo.leftEyebrow", "Live Demo")}
            </p>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-snug">
              {t("demo.leftHeadline", "See Fyrris in 15 minutes")}
            </h2>
            <p className="mt-3 text-gray-400 leading-relaxed">
              {t("demo.leftSubhead", "A quick personal walkthrough — no slides, no pressure.")}
            </p>
          </div>

          <ul className="space-y-4">
            {[
              {
                icon: MonitorPlay,
                text: t("demo.leftBullet1", "We'll walk you through the full AR experience"),
                color: EMBER,
              },
              {
                icon: Package,
                text: t("demo.leftBullet2", "See how product onboarding works"),
                color: TEAL,
              },
              {
                icon: MessageCircle,
                text: t("demo.leftBullet3", "Get your questions answered live"),
                color: TEAL,
              },
            ].map(({ icon: Icon, text, color }) => (
              <li key={text} className="flex items-start gap-3">
                <div
                  className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${color}22` }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color }} />
                </div>
                <span className="text-sm text-gray-300 leading-relaxed">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-gray-600">
          {t("demo.leftFooter", "15 minutes. No commitment. No sales pressure.")}
        </p>
      </div>

      {/* ── Right form panel ─────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-start lg:justify-center px-5 py-10 overflow-y-auto bg-gray-950">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8">
          <img src="/fyrris-logo.png" alt="Fyrris" className="h-8 w-auto object-contain mx-auto" />
        </div>

        <div className="w-full max-w-lg">
          <h1 className="text-2xl font-bold text-white mb-1">
            {t("demo.title", "Book a Demo")}
          </h1>
          <p className="text-gray-400 text-sm mb-8">
            {t("demo.subtitle", "Fill in your details and we'll be in touch within one business day.")}
          </p>

          <form onSubmit={onSubmit} noValidate className="space-y-5">
            <input
              type="text"
              name="_hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />
            {turnstile.enabled && <div ref={turnstile.ref} />}
            
            {/* First + Last */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  {t("demo.firstName", "First name")} <span style={{ color: EMBER }}>*</span>
                </label>
                <input
                  type="text"
                  autoComplete="given-name"
                  placeholder={t("demo.firstNamePlaceholder", "Jane")}
                  value={form.firstName}
                  onChange={(e) => set("firstName", e.target.value)}
                  className={inputCls(errors.firstName)}
                />
                {errors.firstName && <FieldError msg={errors.firstName} />}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  {t("demo.lastName", "Last name")} <span style={{ color: EMBER }}>*</span>
                </label>
                <input
                  type="text"
                  autoComplete="family-name"
                  placeholder={t("demo.lastNamePlaceholder", "Smith")}
                  value={form.lastName}
                  onChange={(e) => set("lastName", e.target.value)}
                  className={inputCls(errors.lastName)}
                />
                {errors.lastName && <FieldError msg={errors.lastName} />}
              </div>
            </div>

            {/* Store name */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t("demo.storeName", "Business / Store name")} <span style={{ color: EMBER }}>*</span>
              </label>
              <input
                type="text"
                autoComplete="organization"
                placeholder={t("demo.storeNamePlaceholder", "e.g. Hearth & Home Retailers")}
                value={form.storeName}
                onChange={(e) => set("storeName", e.target.value)}
                className={inputCls(errors.storeName)}
              />
              {errors.storeName && <FieldError msg={errors.storeName} />}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t("demo.email", "Email address")} <span style={{ color: EMBER }}>*</span>
              </label>
              <input
                type="email"
                autoComplete="email"
                placeholder={t("demo.emailPlaceholder", "jane@yourstore.com")}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className={inputCls(errors.email)}
              />
              {errors.email && <FieldError msg={errors.email} />}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t("demo.phone", "Phone number")} <span style={{ color: EMBER }}>*</span>
              </label>
              <input
                type="tel"
                autoComplete="tel"
                placeholder={t("demo.phonePlaceholder", "e.g. 416-555-0100")}
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className={inputCls(errors.phone)}
              />
              {errors.phone && <FieldError msg={errors.phone} />}
            </div>

            {/* City + State */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  {t("demo.city", "City")} <span style={{ color: EMBER }}>*</span>
                </label>
                <input
                  type="text"
                  autoComplete="address-level2"
                  placeholder={t("demo.cityPlaceholder", "e.g. Toronto")}
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  className={inputCls(errors.city)}
                />
                {errors.city && <FieldError msg={errors.city} />}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  {t("demo.stateProvince", "State / Province")} <span style={{ color: EMBER }}>*</span>
                </label>
                <input
                  type="text"
                  autoComplete="address-level1"
                  placeholder={t("demo.stateProvincePlaceholder", "e.g. ON")}
                  value={form.stateProvince}
                  onChange={(e) => set("stateProvince", e.target.value)}
                  className={inputCls(errors.stateProvince)}
                />
                {errors.stateProvince && <FieldError msg={errors.stateProvince} />}
              </div>
            </div>

            {/* Catalogue size */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t("demo.catalogueSize", "Approximate number of products in your catalogue")}
              </label>
              <select
                value={form.catalogueSize}
                onChange={(e) => set("catalogueSize", e.target.value as CatalogueSize)}
                className={[
                  inputCls(),
                  "appearance-none cursor-pointer",
                  form.catalogueSize === "" ? "text-gray-500" : "text-white",
                ].join(" ")}
              >
                <option value="" disabled>{t("demo.catalogueSizePlaceholder", "Select one…")}</option>
                {CATALOGUE_SIZES.map((s) => (
                  <option key={s} value={s} className="bg-gray-900 text-white">{s}</option>
                ))}
              </select>
            </div>

            {/* Referral */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t("demo.referral", "How did you hear about us?")}
              </label>
              <select
                value={form.referral}
                onChange={(e) => set("referral", e.target.value as ReferralOption)}
                className={[
                  inputCls(),
                  "appearance-none cursor-pointer",
                  form.referral === "" ? "text-gray-500" : "text-white",
                ].join(" ")}
              >
                <option value="" disabled>{t("demo.referralPlaceholder", "Select one…")}</option>
                {REFERRAL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} className="bg-gray-900 text-white">
                    {t(`signup.referral_${opt.toLowerCase().replace(/[^a-z]/g, "_")}`, opt)}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t("demo.message", "Message / questions")}{" "}
                <span className="text-gray-600">{t("demo.optional", "(optional)")}</span>
              </label>
              <textarea
                rows={4}
                placeholder={t("demo.messagePlaceholder", "Anything you'd like us to cover in the demo…")}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                className={[inputCls(), "resize-none"].join(" ")}
              />
            </div>

            {/* Server error */}
            {serverError && (
              <div className="rounded-lg border border-red-900/40 bg-red-900/20 px-4 py-3 text-sm text-red-400">
                {serverError}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center rounded-full py-3 text-sm font-semibold text-white whitespace-nowrap transition hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.25)" }}
            >
              {busy
                ? t("demo.submitting", "Submitting…")
                : t("demo.submit", "Book My Demo")}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            {t("demo.noCommitment", "15 minutes. No commitment. No sales pressure.")}
          </p>

          <p className="mt-3 text-center text-sm text-gray-500">
            {t("demo.generalEnquiry", "Have a general question instead?")}{" "}
            <Link
              to={locale === "es" ? "/es/contacto" : locale === "fr" ? "/fr/contact" : "/contact"}
              className="font-medium underline underline-offset-2 transition-opacity hover:opacity-75"
              style={{ color: EMBER }}
            >
              {t("demo.contactLink", "Contact us")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function FieldError({ msg }: { msg: string }) {
  return <p className="mt-1 text-xs text-red-400">{msg}</p>;
}
